import { CLASS_NAME } from 'constants/class-names'
import { EventType } from 'constants/enums'
import { DOMUtils } from 'utils/dom'
import { TimeUtils } from 'utils/time'
import { StringUtils } from 'utils/string'
import { CSSUtils } from 'utils/css'
import { Page } from 'utils/page'
import { IVideoPlaylistItem } from './video-playlist'
import { JWPlayer } from '../player/jw-player'
import { YouTubePlayer } from '../player/yt-player'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { getEmbedConfig } from '../util'
import {
    YTPlayerConfig,
    VideoEventMap,
    YTPlayerConfigEvent,
    JWPlayerConfigEvent
} from '../types'
import { PlaylistStore } from './store'
import { AbstractPlayer } from '../player/abstract-player'
import { YTPlayerVars } from '../constants'
import { logger } from 'utils/logger'

const {
    common: commonClass,
    videoPlaylist: videoPlaylistClass,
    videoPlaylist: { leadvideo: leadvideoClass }
} = CLASS_NAME

export class LeadVideoComponent extends ComponentBase {
    public player: AbstractPlayer

    protected autoplay: 1 | 0 = 0

    // For the interactions with the first loaded lead video
    private playedOnHover = false

    private leadVideo: HTMLElement

    /**
     * Initialize
     *
     * @param {HTMLElement} playlist
     */
    public constructor(private playlist: HTMLElement) {
        super()
        const leadVideo = this.getElement(`.${leadvideoClass._}`, playlist)
        if (!leadVideo) {
            return
        }
        this.leadVideo = leadVideo
        PlaylistStore.getInstance().addLeadVideo(this)
    }

    /**
     * Get the leading video wrapper element
     *
     * @return {Nullable<HTMLElement>}
     */
    private get wrapper(): Nullable<HTMLElement> {
        return this.getElement(`.${leadvideoClass.wrapper}`, this.playlist)
    }

    /**
     * Get the leading video section title element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get title(): Nullable<HTMLElement> {
        return this.getElement(`.${leadvideoClass.title}`, this.leadVideo)
    }

    /**
     * Get the leading video section meta data parent element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get meta(): Nullable<HTMLElement> {
        return this.getElement(`.${videoPlaylistClass.meta}`, this.leadVideo)
    }

    /**
     * Get the leading video section meta publish time element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get pubTime(): Nullable<HTMLElement> {
        return this.getElement(
            `.${videoPlaylistClass.metaPubtime}`,
            this.leadVideo
        )
    }

    /**
     * Get the leading video section meta duration element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get duration(): Nullable<HTMLElement> {
        return this.getElement(
            `.${videoPlaylistClass.metaDuration}`,
            this.leadVideo
        )
    }

    /**
     * Get the hero list to resize the video component
     *
     * @return {Nullable<HTMLElement>}
     */
    private get heroList(): Nullable<HTMLElement> {
        return this.getElement(`.${leadvideoClass.heroList}`)
    }

    /**
     * Get the queue title
     *
     * @return {Nullable<HTMLElement>}
     */
    private get queueTitle(): Nullable<HTMLHeadingElement> {
        return this.getElement<HTMLHeadingElement>(
            `.${videoPlaylistClass.title}`,
            this.playlist
        )
    }

    /**
     * Get the more video element
     *
     * @return {Nullable<HTMLElement>}
     */
    private get moreVideoLink(): Nullable<HTMLElement> {
        return this.getElement(`.${leadvideoClass.moreVideo}`, this.playlist)
    }

    /**
     * Load YouTube Iframe API
     * initialize player instance
     */
    private async initYouTubePlayer(): Promise<void> {
        const leadVideo = PlaylistStore.getInstance().playlist

        const element = document.getElementById(
            `leadVideoIFrame${leadVideo.uniqueId}`
        )
        const videoId =
            leadVideo.playlistData.items[leadVideo.currentIndex].videoId
        const playlist = leadVideo.playlistData.items.map(
            (item) => item.videoId
        )
        // Add `playlist` parameter with video IDs to play
        // so the videos specified in the playlist parameter will play thereafter
        // even though the video is in multiple playlist
        // https://developers.google.com/youtube/player_parameters#playlist
        const options: YTPlayerConfig = {
            videoId,
            playerVars: {
                ...YTPlayerVars,
                autoplay: this.autoplay,
                playlist: playlist.join(',')
            },
            embedConfig: await getEmbedConfig()
        }
        const events: YTPlayerConfigEvent = {
            onReady: (): void => this.onReady(),
            onStateChange: (event: YT.OnStateChangeEvent): void => {
                const playerStatus: YT.PlayerState = event.data

                if (playerStatus === 1) {
                    this.onPlay()
                }
            }
        }

        if (element) {
            this.player = new YouTubePlayer(
                element,
                videoId,
                options,
                events,
                !!this.autoplay,
                true
            )
        }
    }

    /**
     * Load Jw script
     * initialize player instance
     */
    private loadAndInitJwPlayer(): void {
        window.addEventListener(VideoEventMap.JW_SKIP, ((
            event: CustomEvent
        ) => {
            this.renderLeadMetadataUI(
                event.detail.title,
                TimeUtils.epochToISO(event.detail.pubdate),
                TimeUtils.secondsToDurationISO(event.detail.duration)
            )
        }) as EventListener)

        const element = this.getElement(`.${leadvideoClass.jwInjection}`)
        const videoId = PlaylistStore.getInstance().current

        if (!element || !videoId) {
            return
        }

        const events: JWPlayerConfigEvent = {
            onReady: () => this.onReady(),
            onPlay: () => this.onPlay()
        }

        this.player = new JWPlayer(
            element,
            videoId,
            events,
            !!this.autoplay,
            true,
            this.leadVideo.dataset.version || ''
        )
    }

    /**
     * handle the response from the playlist ID call
     * Build HTML and attach events to newly created elements
     * then turn off loading animation
     *
     * @return {boolean} is the playlist leadlist
     */
    public init(): boolean {
        // build the lead video section with a unique id (more than one on page)
        this.leadVideo.innerHTML = this.renderLeadVideo()
        this.resizeQueueListHeight()

        StringUtils.isPlaylistJw(PlaylistStore.getInstance().current)
            ? this.loadAndInitJwPlayer()
            : void this.initYouTubePlayer()

        // remove loading animation
        CSSUtils.removeClass(this.playlist, CLASS_NAME.list.listLoading)

        return true
    }

    /**
     * Handle the Event for player change
     */
    protected onPlay(): void {
        const leadVideo = PlaylistStore.getInstance().playlist

        if (this.player.index !== leadVideo.currentIndex) {
            leadVideo.currentIndex = this.player.index
            this.updateLeadVideoDetails()
        }
    }

    // Desktop hover listener on category/index pages
    private createHoverListener(): void {
        // TODO: hover play until Product makes final desicion
        // bug: https://postmediadigital.atlassian.net/browse/CHEET-1380
        const enable = false
        if (enable && this.wrapper) {
            this.wrapper.addEventListener(EventType.MOUSE_ENTER, () =>
                this.playLeadVideo()
            )
        }
    }

    /**
     * Handle the Event for player ready
     */
    public onReady(): void {
        // create play lead video on hover interaction
        this.createHoverListener()
        // queue the rest of the videos in the playlist for the API controls
        this.queueVideolist()

        this.player.unmute()

        if (this.autoplay) {
            this.player.play()
        }

        if (this.autoplay && !this.playedOnHover) {
            this.player.mute()
        }
    }

    /**
     * Load the playlist on the player for use of playlist API functions
     * https://developers.google.com/youtube/iframe_api_reference#Playlist_Queueing_Functions
     */
    public queueVideolist(): void {
        const leadVideo = PlaylistStore.getInstance().playlist

        // 🤡 Gotcha:
        // in youtube iframe api docs type = playlist
        // in pkg @types/youtube type = player
        const playlistArgs: YT.IPlaylistSettings = {
            list: leadVideo.playlistId,
            listType: 'player',
            index: leadVideo.currentIndex
        }
        this.player.cuePlaylist(playlistArgs)
    }

    /**
     * Mute and play video
     * https://developers.google.com/youtube/iframe_api_reference#changing-the-player-volume
     * https://developers.google.com/youtube/iframe_api_reference#playing-a-video
     * on desktop only will this work / is expected to work see API note:
     * https://developers.google.com/youtube/iframe_api_reference?hl=en#Mobile_considerations
     */
    public playLeadVideo(): void {
        if (!this.playedOnHover) {
            this.player.mute()
            this.player.play()
            this.playedOnHover = true
        }
    }

    /**
     * Update title, time ago and duration meta information on the lead video section
     * Using the currently saved playing index
     */
    private updateLeadVideoDetails(): void {
        const leadVideo = PlaylistStore.getInstance().playlist

        const newDetails: IVideoPlaylistItem =
            leadVideo.playlistData.items[leadVideo.currentIndex]

        if (!newDetails) {
            CSSUtils.addClass(this.meta, commonClass.hidden)
            CSSUtils.addClass(this.title, commonClass.hidden)
            return
        }
        this.renderLeadMetadataUI(
            newDetails.title,
            newDetails.publishedAt,
            newDetails.duration
        )
    }

    private renderLeadMetadataUI(
        title: string,
        publishedAt: string,
        duration: string
    ): void {
        CSSUtils.removeClass(this.meta, commonClass.hidden)
        if (this.title) {
            CSSUtils.removeClass(this.title, commonClass.hidden)
            this.title.innerHTML = title
        }
        if (this.pubTime) {
            this.pubTime.innerHTML = TimeUtils.renderUITimeAgo(publishedAt)
        }
        if (this.duration) {
            this.duration.innerHTML = TimeUtils.renderUIISODuration(duration)
        }
    }

    public destroy(): void {
        this.player.destroy()
    }

    /**
     * Build the lead video article content for the playlist
     * Using the given unique id (data-unique-id)
     */
    private renderLeadVideo(): string {
        const leadVideo = PlaylistStore.getInstance().playlist
        const firstItem = leadVideo.playlistData.items[0]
        const dataEvtVal = (
            video_platform: string,
            autoplay: boolean,
            pageType: string
        ): string => {
            return `{"video_autoplay": "${autoplay}","video_location": "${pageType} page","video_platform": "${video_platform}",
            "control_fields": { "mparticle": {
                                "keys": {"video_autoplay":"Video Autoplay", "video_location": "Video Location", "video_platform": "Video Platform" }
                            }}}`
        }

        const jwTemplate = `
            <div class="${leadvideoClass.jwWrapper}"
                id="leadVideoIFrame${leadVideo.uniqueId}"
                aria-labelledby="leadVideoTitle${leadVideo.uniqueId}"

                data-media=${PlaylistStore.getInstance().current}>
                <div class="${leadvideoClass.jwInjection}"
                    data-evt-val='${dataEvtVal(
                        'JW Player',
                        !!this.autoplay,
                        Page.type
                    )}'></div>
            </div>
            `
        const ytTemplate = `
                <div
                    id="leadVideoIFrame${leadVideo.uniqueId}"
                    class="${leadvideoClass.iframe}"
                    data-evt-val='${dataEvtVal(
                        'Youtube',
                        !!this.autoplay,
                        Page.type
                    )}'
                    aria-labelledby="leadVideoTitle${leadVideo.uniqueId}">
                </div>
        `
        console.error('jwTemplate ', jwTemplate, ytTemplate)
        const leadPlayerTemplate = StringUtils.isPlaylistJw(
            leadVideo.playlistId
        )
            ? jwTemplate
            : ytTemplate
        const template = `
            <div class="${leadvideoClass.bg}">
                ${leadPlayerTemplate}
            </div>
            <h3
                id="leadVideoTitle${leadVideo.uniqueId}"
                class="${leadvideoClass.title}"
            >
                ${firstItem.title}
            </h3>
            <div class="${videoPlaylistClass.meta}">
                <p class="${videoPlaylistClass.metaPubtime}">
                    ${TimeUtils.renderUITimeAgo(firstItem.publishedAt)}
                </p>
                <p class="${videoPlaylistClass.metaDuration}">
                    ${TimeUtils.renderUIISODuration(firstItem.duration)}
                </p>
            </div>`
        return template
    }

    /**
     * Resize list height if it needs
     */
    protected resizeQueueListHeight(): void {
        // Only for the large screen
        // eslint-disable-next-line no-magic-numbers
        if (window.screen.width < 1200) {
            return
        }

        const leadVideo = PlaylistStore.getInstance().playlist
        const queueList = leadVideo.queueList

        if (
            !this.heroList ||
            !this.wrapper ||
            !this.queueTitle ||
            !this.moreVideoLink
        ) {
            return
        }

        const heightHero = DOMUtils.outerHeight(this.heroList)
        const heightWrapper = DOMUtils.outerHeight(this.wrapper)
        const heightTitle = DOMUtils.outerHeight(this.queueTitle)
        const heightMore = DOMUtils.outerHeight(this.moreVideoLink)

        queueList.style.height = `calc(${heightHero}px - ${heightWrapper}px - ${heightTitle}px - ${heightMore}px)`
    }

    /**
     * User clicks an item
     *
     * @param {string} playlistId
     * @param {number} index
     */
    public onClick(playlistId: string, index: number): void {
        // Another playlist clicked
        if (PlaylistStore.getInstance().current !== playlistId) {
            this.playFromNewPlaylist(playlistId, index)
        } else {
            // 2. Home/Category page - single playlist
            this.playFromSamePlaylist(index)
        }
        this.updateLeadVideoDetails()
    }

    /**
     * User player interaction involving switching between playlists
     * Currently more that one playlist are possible on Video Centre page
     * ex: https://nationalpost.com/video-centre/
     *
     * @param {string} playlistId
     * @param {number} index
     */
    private playFromNewPlaylist(playlistId: string, index: number): void {
        PlaylistStore.getInstance().current = playlistId
        if (StringUtils.isPlaylistJw(playlistId)) {
            logger('warn', 'JW on video centre page pending', index)
        } else {
            // YT
            this.autoplay = 1
            // YT should be defined when this callback is available
            this.player.destroy()
            this.init()
        }
    }

    /**
     * User player interaction without switching playlists
     * Currently possible for Video Widget on category/index pages or Video Centre page
     * ex: https://nationalpost.com/
     * ex: https://nationalpost.com/category/news/
     * ex: https://nationalpost.com/video-centre/
     *
     * @param {string} playlistId
     * @param {number} index
     */
    private playFromSamePlaylist(index: number): void {
        this.player.playVideoAt(index)
    }
}
